import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import classNames from "classnames/bind";
// posthog loaded dynamically to keep analytics out of initial bundle
import ModalWindow from "../modal-window";
import { useCreateSubscriptionCheckoutMutation } from "../../../api/paymentsApi";
import { trackBeginCheckout } from "../../../utils/analytics";
import { RootState } from "../../../store";
import styles from "./UpgradeModal.module.scss";
import type { BillingInterval } from "@nicsmith/shared_types";

const cx = classNames.bind(styles);

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEATURES = [
  { icon: "fa-lock-open" },
  { icon: "fa-redo" },
  { icon: "fa-robot" },
  { icon: "fa-spell-check" },
  { icon: "fa-download" },
  { icon: "fa-ban" },
];

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("month");
  const [createCheckout, { isLoading }] = useCreateSubscriptionCheckoutMutation();
  const isAuthenticated = useSelector((state: RootState) => state.session.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();

  const handleUpgrade = async () => {
    // If not authenticated, redirect to sign-up with a return URL
    if (!isAuthenticated) {
      onClose();
      navigate(`/signup?returnTo=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }

    const price = billingInterval === "month" ? 14.99 : 139.99;
    const item = billingInterval === "month" ? "Premium Monthly" : "Premium Yearly";

    // GA4 begin_checkout event
    trackBeginCheckout("subscription", item, billingInterval, price);

    // PostHog purchase intent event
    import("posthog-js").then(({ default: posthog }) => {
      posthog.capture("subscription_checkout_started", {
        value: price,
        currency: "EUR",
        item,
        billing_interval: billingInterval,
      });
    });

    try {
      const result = await createCheckout({
        billing_interval: billingInterval,
        success_url: `${window.location.origin}/dashboard?subscription=success`,
        cancel_url: `${window.location.origin}/dashboard?subscription=canceled`,
      }).unwrap();

      if (result.url) {
        window.location.href = result.url;
      }
    } catch {
      // Error handled by RTK Query baseQuery popup
    }
  };

  return (
    <ModalWindow
      isOpenModal={isOpen}
      closeModal={onClose}
      style={{ maxWidth: 480, width: "94vw", padding: 0, borderRadius: 20, border: "none" }}
    >
      <div className={cx("root")}>
        <button className={cx("close")} onClick={onClose} aria-label={t("common.close", "Close")}>
          <i className="fas fa-times"></i>
        </button>

        {/* Header */}
        <div className={cx("header")}>
          <div className={cx("crown")}>
            <i className="fas fa-crown"></i>
          </div>
          <h2 className={cx("title")}>
            {t("subscription.upgradeTitle", "Upgrade to Premium")}
          </h2>
          <p className={cx("subtitle")}>
            {t("subscription.upgradeSubtitle", "Unlock your full potential with premium features")}
          </p>
        </div>

        {/* Billing toggle */}
        <div className={cx("toggleWrap")}>
          <div className={cx("toggle")}>
            <button
              className={cx("toggleBtn", { active: billingInterval === "month" })}
              onClick={() => setBillingInterval("month")}
            >
              {t("subscription.monthly", "Monthly")}
            </button>
            <button
              className={cx("toggleBtn", { active: billingInterval === "year" })}
              onClick={() => setBillingInterval("year")}
            >
              {t("subscription.yearly", "Yearly")}
              <span className={cx("saveBadge")}>-22%</span>
            </button>
          </div>
        </div>

        {/* Price */}
        <div className={cx("priceCard")}>
          {billingInterval === "month" ? (
            <>
              <p className={cx("amount")}>
                <span className={cx("currency")}>€</span>14.99
                <span className={cx("period")}>/ {t("subscription.mo", "mo")}</span>
              </p>
              <p className={cx("note")}>
                {t("subscription.billedMonthly", "Billed monthly, cancel anytime")}
              </p>
            </>
          ) : (
            <>
              <p className={cx("amount")}>
                <span className={cx("currency")}>€</span>139.99
                <span className={cx("period")}>/ {t("subscription.yr", "yr")}</span>
              </p>
              <p className={cx("note")}>
                {t("subscription.billedYearly", "That's just €11.67/mo — billed yearly")}
              </p>
            </>
          )}
        </div>

        {/* Features */}
        <div className={cx("featuresWrap")}>
          <p className={cx("featuresLabel")}>
            {t("subscription.whatsIncluded", "Everything you get")}
          </p>
          <div className={cx("featuresGrid")}>
            {FEATURES.map((f, i) => (
              <div key={i} className={cx("featureItem")}>
                <div className={cx("featureIcon")}>
                  <i className={`fas ${f.icon}`}></i>
                </div>
                <span className={cx("featureText")}>{t(`subscription.feature${i}`)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={cx("ctaWrap")}>
          <button
            className={cx("cta")}
            onClick={handleUpgrade}
            disabled={isLoading}
          >
            {isLoading ? (
              <span>{t("common.loading", "Loading...")}</span>
            ) : (
              <>
                <span>{t("subscription.upgradeCta", "Upgrade to Premium")}</span>
                <i className="fas fa-arrow-right"></i>
              </>
            )}
          </button>
        </div>
      </div>
    </ModalWindow>
  );
};

export default UpgradeModal;
